/*
  Ticket modal: pick a duration and quantity, confirm who is coming, go to
  checkout.

  Buyer details are attached as line item properties so the ticketing app can
  put them on the ticket, and so the cart drawer can show which event the line
  is for. Newsletter opt-in and the same details are mirrored into the Google
  Sheet used by the other forms; a failure there must never block the purchase.

  WHERE THE ATTENDEE EMAIL LIVES, and why in three places at once.

  The ticket is delivered to the ORDER's email, which is not necessarily the
  address typed here. Two ways it diverges, both observed: checkout accepts a
  phone number instead of an email (20 orders had none at all), and an express
  wallet button — Apple Pay, Google Pay, Shop Pay — replaces the contact email
  with the wallet's own (172 orders). Neither is reachable from a theme: those
  buttons live on Shopify's own checkout, inside a closed shadow DOM, and cannot
  be hidden by any theme code or store setting short of switching the wallet off
  store-wide. `?checkout[email]=` only prefills a field a wallet then overwrites
  — and as of 2026-08 Shopify has an open, staff-confirmed regression breaking
  that prefill outright.

  So the typed address is carried by data a wallet never touches:

    · `_ticket_email` — a PRIVATE line item property (the leading underscore is
      Shopify's own convention for "do not show this at checkout"). Survives to
      the order as a line-item custom attribute, whatever happens to the contact
      email, and its key is ASCII so it reads the same on /pl and /en.
    · cart attribute `ticket_email` — the same value at cart level, which lands
      in the order's Additional details where staff and the ticketing app's
      dashboard can see it, and which the cart drawer reads to keep the checkout
      prefill working for a buyer who left and came back.
    · `?checkout[email]=` — kept as a convenience prefill only. Never trusted.

  The visible, localized properties stay for humans reading the cart drawer; the
  underscore-prefixed ones are what any script or automation should read.

  Structure intentionally mirrors join-modal.js (open/close, focus trap, body
  lock, ?ticket_modal=1 deep link) so the two behave identically.
*/
(function () {
  'use strict';

  // One copy of this file per page, however many modals load it. A page that
  // sells several events renders a modal per event, and each one carries its
  // own <script> tag; a second run would bind every trigger twice.
  if (window.GalasTicketModals) return;
  var registry = window.GalasTicketModals = {};

  // Private line item properties — ASCII, stable across locales, hidden from
  // checkout. Anything downstream (Flow, scripts/fix-ticket-emails.py, an
  // order export) should key off these, never off the translated labels.
  var PROP_EMAIL = '_ticket_email';
  var PROP_ATTENDEE = '_ticket_attendee';
  var PROP_EVENT_ID = '_ticket_event_id';
  var PROP_SUBMISSION = '_ticket_submission';

  var ATTR_EMAIL = 'ticket_email';
  var ATTR_ATTENDEE = 'ticket_attendee';

  // Survives a tariff reload, an accidental refresh and a bounce back from
  // checkout. Session-scoped: this is contact data, not a preference, so it
  // should not outlive the tab.
  var FORM_STORAGE_KEY = 'galas:ticket-form';

  // How long a `ticket_t` marker vouches for the freshness of a render.
  var REFRESH_TRUST_MS = 5 * 60 * 1000;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /*
    MORE THAN ONE MODAL. The festival sells one product picked in theme
    settings, through the modal in layout/theme.liquid — key '' (empty). A page
    that sells several events at once (a tour: one concert per city, each its
    own product and its own `events` row) renders one more modal per event,
    keyed by its block id, from sections/event-tickets.liquid.

    So everything below is built per modal: `createTicketModal` is called once
    for each `[data-ticket-modal]`, and a trigger names the modal it opens with
    the value of `data-ticket-trigger` — empty for the festival one, which is
    every trigger that existed before this. `?ticket_modal=1` still opens the
    festival modal; `?ticket_modal=<key>` opens that one.
  */
  function createTicketModal(root_) {
    var modalKey = root_.getAttribute('data-ticket-modal-key') || '';
    var modalEl = null;
    var panelEl = null;
    var formEl = null;
    var titleEl = null;
    var optionsEl = null;
    var qtyEl = null;
    var contactEl = null;
    var qtyLabelEl = null;
    var greetingEl = null;
    var greetingNameEl = null;
    var nameEl = null;
    var emailEl = null;
    var newsletterEl = null;
    var newsletterRowEl = null;
    var errorEl = null;
    var submitEl = null;
    var submitLabelEl = null;
    var totalEl = null;
    var countdownDotEl = null;
    var countdownTextEl = null;
    var multiNoteEl = null;

    var sheets = { endpoint: '', token: '' };
    var countdownTimer = null;

    var state = {
      open: false,
      lastOpener: null,
      submitting: false,
      variantId: '',
      duration: '',
      priceCents: 0,
      quantity: 1,
      maxQuantity: 10,
      eventLabel: '',
      eventId: '',
      productId: '',
      isLoggedIn: false,
      customerName: '',
      customerEmail: '',
      acceptsMarketing: false,
      // Set once per successful sale so a retry after a network error cannot
      // subscribe the buyer twice or add a second row to the sheet.
      sideEffectsDone: false
    };

    function t(key, fallback) {
      var dict = window.TicketModalI18n || {};
      return dict[key] || fallback;
    }

    function root() {
      var routes = (window.Shopify || {}).routes || {};
      return routes.root || '/';
    }

    function init() {
      modalEl = root_;

      panelEl = modalEl.querySelector('[data-ticket-modal-panel]');
      formEl = modalEl.querySelector('[data-ticket-modal-form]');
      titleEl = modalEl.querySelector('[data-ticket-modal-title]');
      optionsEl = modalEl.querySelector('[data-ticket-options]');
      qtyEl = modalEl.querySelector('[data-ticket-qty]');
      contactEl = modalEl.querySelector('[data-ticket-contact]');
      qtyLabelEl = modalEl.querySelector('[data-ticket-qty-label]');
      greetingEl = modalEl.querySelector('[data-ticket-logged-in]');
      greetingNameEl = modalEl.querySelector('[data-ticket-customer-name]');
      nameEl = modalEl.querySelector('[data-ticket-name]');
      emailEl = modalEl.querySelector('[data-ticket-email]');
      newsletterEl = modalEl.querySelector('[data-ticket-newsletter]');
      newsletterRowEl = modalEl.querySelector('[data-ticket-newsletter-row]');
      errorEl = modalEl.querySelector('[data-ticket-error]');
      submitEl = modalEl.querySelector('[data-ticket-submit]');
      submitLabelEl = modalEl.querySelector('[data-ticket-submit-label]');
      totalEl = modalEl.querySelector('[data-ticket-total]');
      countdownDotEl = modalEl.querySelector('[data-ticket-countdown-dot]');
      countdownTextEl = modalEl.querySelector('[data-ticket-countdown-text]');
      multiNoteEl = modalEl.querySelector('[data-ticket-multi-note]');

      if (!panelEl || !formEl) return;

      sheets.endpoint = modalEl.getAttribute('data-ticket-sheets-endpoint') || '';
      sheets.token = modalEl.getAttribute('data-ticket-sheets-token') || '';
      state.maxQuantity = parseInt(modalEl.getAttribute('data-ticket-max'), 10) || 10;
      state.productId = modalEl.getAttribute('data-ticket-product-id') || '';
      state.eventId = modalEl.getAttribute('data-ticket-event-id') || '';

      readCustomer();
      applyLoginState();
      restoreForm();
      selectFirstOption();
      startCountdown();

      modalEl.querySelectorAll('[data-ticket-modal-close]').forEach(function (el) {
        el.addEventListener('click', function (e) { e.preventDefault(); close(); });
      });

      if (optionsEl) {
        optionsEl.addEventListener('click', function (e) {
          var option = e.target.closest ? e.target.closest('[data-ticket-variant]') : null;
          if (option) selectOption(option);
        });

        // A radiogroup is expected to move with the arrow keys and to be a single
        // tab stop; without this each card was its own stop and the arrows did
        // nothing, which is not the control the roles promise.
        optionsEl.addEventListener('keydown', function (e) {
          var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
          if (keys.indexOf(e.key) === -1) return;
          var all = options();
          if (all.length < 2) return;
          var current = all.indexOf(document.activeElement);
          if (current === -1) return;
          e.preventDefault();
          var forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
          var next = all[(current + (forward ? 1 : all.length - 1)) % all.length];
          selectOption(next);
          next.focus({ preventScroll: true });
        });
      }

      modalEl.querySelectorAll('[data-ticket-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          setQuantity(state.quantity + parseInt(btn.getAttribute('data-ticket-qty-step'), 10));
        });
      });

      if (qtyEl) {
        qtyEl.addEventListener('change', function () { setQuantity(parseInt(qtyEl.value, 10)); });
        qtyEl.addEventListener('blur', function () { setQuantity(parseInt(qtyEl.value, 10)); });
      }

      [nameEl, emailEl].forEach(function (el) {
        if (el) el.addEventListener('input', saveForm);
      });

      // The test gate has to react as the address is typed; waiting for submit
      // would leave the button dead with no explanation.

      formEl.addEventListener('submit', onSubmit);

      // Back from checkout is a bfcache restore on both Safari and Chrome: the JS
      // heap returns exactly as it was left, so the modal came back still open
      // with a disabled button reading "Preparing your ticket…" and no way out
      // but Escape. Nothing re-checked the tariff on that path either.
      window.addEventListener('pageshow', function (e) {
        if (!e.persisted) return;
        setSubmitting(false);
        state.sideEffectsDone = false;
        if (state.open) close();
      });

      // A suspended tab does not run the countdown's interval, so it can wake up
      // long past the deadline still showing yesterday's prices.
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') return;
        if (tariffExpired()) reloadForNewTariff(state.open);
      });

      if (deepLinkKey() === modalKey) {
        /*
          A deep link may carry an anchor too — `?ticket_modal=1#upcoming-event`
          is the shareable "open the modal on the home page" link. Opening the
          modal locks body scrolling immediately, which cuts short the smooth
          hash scroll the browser has just started and leaves the page at the
          top; closing the modal would then reveal the wrong section. So the jump
          is made instantly, before the lock.
        */
        deepLinkHash = window.location.hash;
        jumpToHash(deepLinkHash);
        open(null);
      }
    }

    /* ── Customer ── */

    function readCustomer() {
      var g = window.__galas || {};
      if (!g.customer) return;
      state.isLoggedIn = true;
      state.customerName = g.customerName
        || [g.customer.first_name, g.customer.last_name].filter(Boolean).join(' ')
        || '';
      state.customerEmail = g.customer.email || '';
      state.acceptsMarketing = !!g.customer.accepts_marketing;
    }

    // A signed-in customer already told us who they are, so we confirm instead of
    // asking again — same pattern as the join modal.
    //
    // Only when we actually know their name, though. An account created by the
    // newsletter opt-in path has no first or last name, and hiding the fields for
    // it produced a purchase with no way forward: submit failed on "enter your
    // name" pointing at an input the buyer could not see.
    function applyLoginState() {
      var known = state.isLoggedIn && state.customerName.length > 1;

      if (contactEl) contactEl.hidden = known;
      if (greetingEl) greetingEl.hidden = !known;
      // The row carries either the label or the recipient, never both.
      if (qtyLabelEl) qtyLabelEl.hidden = known;

      // Nothing to ask an already-subscribed customer.
      if (state.acceptsMarketing && newsletterRowEl) {
        newsletterRowEl.hidden = true;
        if (newsletterEl) newsletterEl.checked = false;
      }
      if (greetingNameEl) greetingNameEl.textContent = state.customerName || state.customerEmail;

      if (!state.isLoggedIn) return;
      if (nameEl && state.customerName) nameEl.value = state.customerName;
      if (emailEl && state.customerEmail) emailEl.value = state.customerEmail;
    }

    /* ── Remembering what was typed ── */

    // The one purchase flow in the theme that used to forget the buyer — and the
    // only one that reloads its own page mid-form, when a tariff turns over. The
    // buyer came back to an empty form at a higher price. Mirrors the donate and
    // join modals, which already keep `galas:donor` / `galas:member`.
    function saveForm() {
      if (!window.sessionStorage) return;
      try {
        window.sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({
          name: nameEl ? nameEl.value : '',
          email: emailEl ? emailEl.value : '',
          quantity: state.quantity,
          newsletter: newsletterEl ? !!newsletterEl.checked : false
        }));
      } catch (_) { /* private mode, quota — never worth failing a purchase over */ }
    }

    function restoreForm() {
      if (!window.sessionStorage) return;
      var raw;
      try { raw = window.sessionStorage.getItem(FORM_STORAGE_KEY); } catch (_) { return; }
      if (!raw) return;

      var saved;
      try { saved = JSON.parse(raw); } catch (_) { return; }
      if (!saved) return;

      // A signed-in buyer's own account details win over anything stashed here.
      if (nameEl && !nameEl.value && saved.name) nameEl.value = saved.name;
      if (emailEl && !emailEl.value && saved.email) emailEl.value = saved.email;
      if (saved.quantity) setQuantity(parseInt(saved.quantity, 10));
      // Consent is deliberately NOT restored as checked-by-default; it is only
      // carried forward when the buyer had actually ticked it.
      if (newsletterEl && saved.newsletter === true) newsletterEl.checked = true;
    }

    function clearForm() {
      if (!window.sessionStorage) return;
      try { window.sessionStorage.removeItem(FORM_STORAGE_KEY); } catch (_) { /* ignore */ }
    }

    /* ── Options and quantity ── */

    // A disabled card is a city whose sale is closed: listed, never selectable,
    // and skipped by the arrow keys.
    function options() {
      return optionsEl
        ? Array.prototype.slice.call(optionsEl.querySelectorAll('[data-ticket-variant]:not([disabled])'))
        : [];
    }

    // Every product this modal can sell. One for the festival; one per city when
    // the modal is a choice of events (cards carry `data-ticket-product-id`).
    function productIds() {
      var ids = optionsEl
        ? Array.prototype.map.call(optionsEl.querySelectorAll('[data-ticket-product-id]'), function (o) {
          return o.getAttribute('data-ticket-product-id');
        })
        : [];
      if (state.productId && ids.indexOf(state.productId) === -1) ids.push(state.productId);
      return ids.filter(Boolean);
    }

    function selectFirstOption() {
      var all = options();
      var active = all.filter(function (o) { return o.classList.contains('is-active'); })[0] || all[0];
      if (active) selectOption(active);
    }

    function selectOption(option) {
      options().forEach(function (o) {
        var isActive = o === option;
        o.classList.toggle('is-active', isActive);
        o.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
      state.variantId = option.getAttribute('data-ticket-variant') || '';
      state.duration = option.getAttribute('data-ticket-duration') || '';
      state.priceCents = parseInt(option.getAttribute('data-ticket-price-cents'), 10) || 0;
      // A card that is its own event (a tour city) carries the facts the line
      // item needs, and they replace the modal-level ones: picking Wrocław has
      // to file the ticket under Wrocław, whichever button opened the modal.
      if (option.hasAttribute('data-ticket-event-id')) {
        state.productId = option.getAttribute('data-ticket-product-id') || state.productId;
        state.eventId = option.getAttribute('data-ticket-event-id') || '';
        state.eventLabel = option.getAttribute('data-ticket-event-label') || state.eventLabel;
      }
      renderTotal();
      clearError();
    }

    function setQuantity(value) {
      if (isNaN(value)) value = 1;
      state.quantity = Math.min(state.maxQuantity, Math.max(1, value));
      if (qtyEl) qtyEl.value = String(state.quantity);

      modalEl.querySelectorAll('[data-ticket-qty-step]').forEach(function (btn) {
        var step = parseInt(btn.getAttribute('data-ticket-qty-step'), 10);
        var disable = step < 0 ? state.quantity <= 1 : state.quantity >= state.maxQuantity;
        // Disabling the element that currently holds focus drops focus to <body>,
        // outside the panel — and the next Tab then walked the page behind the
        // modal. Hand focus to the input before taking the button away.
        if (disable && document.activeElement === btn && qtyEl) {
          qtyEl.focus({ preventScroll: true });
        }
        btn.disabled = disable;
      });

      // One attendee name goes onto the line, so this only needs saying when the
      // line covers more than one ticket.
      if (multiNoteEl) multiNoteEl.hidden = state.quantity < 2;

      renderTotal();
      saveForm();
      clearError();
    }

    function formatMoney(cents) {
      var amount = cents / 100;
      var text = amount % 1 === 0 ? String(amount) : amount.toFixed(2).replace('.', ',');
      return text + ' ' + t('ticket_modal.currency', 'zł');
    }

    // The total only earns its space once more than one ticket is selected.
    function renderTotal() {
      if (!totalEl) return;
      if (state.quantity > 1 && state.priceCents) {
        totalEl.textContent = formatMoney(state.priceCents * state.quantity);
        totalEl.hidden = false;
      } else {
        totalEl.textContent = '';
        totalEl.hidden = true;
      }
    }

    /* ── Countdown to the next tariff ── */

    // The tier block's heading doubles as the countdown: saying "the price goes
    // up" in a title, a row and a separate timer line was the same sentence three
    // times over. Until JS runs, the heading keeps its static wording.
    function startCountdown() {
      if (!countdownTextEl) return;
      var deadline = deadlineMs();
      if (!deadline) return;

      var tick = function () {
        var left = deadline - Date.now();
        if (left <= 0) {
          if (countdownDotEl) countdownDotEl.hidden = true;
          if (countdownTimer) clearInterval(countdownTimer);
          // The tariff just turned over under this render. With the modal closed
          // we can quietly fetch the new prices; with it open, reloading would
          // wipe a half-typed form, so onSubmit stops the sale instead.
          if (!state.open) reloadForNewTariff(false);
          return;
        }

        var totalSeconds = Math.floor(left / 1000);
        var days = Math.floor(totalSeconds / 86400);
        var hours = Math.floor((totalSeconds % 86400) / 3600);
        var minutes = Math.floor((totalSeconds % 3600) / 60);
        var seconds = totalSeconds % 60;

        // Precision follows the distance: ticking seconds are noise two days out,
        // and the only moment they earn their place is the final hour.
        var value;
        if (days > 0) {
          value = days + ' ' + t('ticket_modal.days_short', 'дн')
            + ' ' + hours + ' ' + t('ticket_modal.hours_short', 'год');
        } else if (hours > 0) {
          value = hours + ' ' + t('ticket_modal.hours_short', 'год')
            + ' ' + minutes + ' ' + t('ticket_modal.minutes_short', 'хв');
        } else {
          var pad = function (n) { return n < 10 ? '0' + n : String(n); };
          value = pad(minutes) + ':' + pad(seconds);
        }

        countdownTextEl.textContent =
          t('ticket_modal.countdown', 'Ціна зросте через') + ' ' + value;
        if (countdownDotEl) countdownDotEl.hidden = false;
      };

      tick();
      countdownTimer = setInterval(tick, 1000);
    }

    /* ── Stale tariff guard ── */

    // The option cards are priced by the server at render time, so any HTML that
    // was rendered before a tariff deadline keeps offering the old price: a page
    // held in Shopify's full-page cache, a tab left open overnight, a back-button
    // restore. `cart/add.js` takes the expired variant without complaint — which
    // is how three "Two days / Early bird" tickets were sold at 00:18 Warsaw,
    // eighteen minutes after that tariff closed.
    //
    // The deadline the countdown ticks down to is exactly the moment this HTML
    // stops being true, so anything past it has to be re-rendered before it can
    // be bought. No deadline means Last Minute — the final tariff, with nothing
    // left to expire into.
    // A device clock running a few minutes fast would read the deadline as passed
    // while the server still renders the old tariff — one refresh, same HTML, and
    // round again. The marker our own reload leaves behind means the render is as
    // fresh as it can get, so it is trusted from then on and the loop is bounded
    // to a single reload.
    //
    // Only while it is FRESH, though. Merely containing `ticket_t=` used to switch
    // the guard off for the life of that page: suspend such a tab past the
    // deadline (a frozen tab runs no interval), wake it, and the pre-deadline HTML
    // sold the expired tariff again — the very thing the guard exists to stop. And
    // `?ticket_t=1` was enough to disable it by hand.
    function alreadyRefreshed() {
      var raw = new URLSearchParams(window.location.search).get('ticket_t');
      var stamp = raw ? Number(raw) : 0;
      if (!stamp) return false;
      var age = Date.now() - stamp;
      return age >= 0 && age < REFRESH_TRUST_MS;
    }

    function deadlineMs() {
      var raw = modalEl.getAttribute('data-ticket-deadline-ms');
      var ms = raw ? Number(raw) : 0;
      return ms > 0 ? ms : 0;
    }

    function tariffExpired() {
      if (alreadyRefreshed()) return false;
      var deadline = deadlineMs();
      if (!deadline) return false;
      return Date.now() >= deadline;
    }

    // Cache-busted, or the reload can be served the very same stale render.
    //
    // `reopen` decides whether the buyer lands back in the modal. It is true when
    // they were mid-purchase, and false when the page simply outlived a tariff
    // with nobody watching: this snippet is rendered on every page, so an
    // unattended homepage tab used to reload itself overnight and pop a purchase
    // modal open on its own. Existing markers are dropped rather than appended, or
    // a page that crossed two deadlines collected a query string of them.
    function reloadForNewTariff(reopen) {
      var url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('ticket_modal');
      url.searchParams.delete('ticket_t');
      // The key, so a reload reopens THIS modal and not the festival one.
      if (reopen) url.searchParams.set('ticket_modal', modalKey || '1');
      url.searchParams.set('ticket_t', String(Date.now()));
      if (reopen) saveForm();
      window.location.replace(url.toString());
    }

    /*
      The anchor a `?ticket_modal=1#…` link arrived with, if any.

      Kept because the jump has to happen twice. Once before the modal opens, and
      once when it closes: locking body scrolling can drop the position the hash
      just set, and the reader closing the modal must be left in front of the buy
      button rather than back at the top of the page. Cleared after use, so a
      modal the reader opens later by clicking a button never moves the page.
    */
    var deepLinkHash = '';

    /**
     * Puts the page at `hash` with no animation.
     *
     * `querySelector` is given a value from the address bar, so it can be a
     * string that is not a valid selector ("#1", "#a b"); that throws, and a
     * throw here would stop the modal from opening at all.
     */
    function jumpToHash(hash) {
      if (!hash) return;
      var target = null;
      try { target = document.querySelector(hash); } catch (_) { return; }
      if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }

    /* ── Open / close ── */

    function openFromTrigger(trigger) {
      var title = trigger.getAttribute('data-ticket-title') || '';
      if (titleEl && title) titleEl.textContent = title;
      state.eventLabel = eventLabel(trigger.getAttribute('data-ticket-event') || title);
      // `data-ticket-select` preselects a card by its event id — the city card
      // on the tour page opens the shared modal on its own city. Re-applying the
      // current card otherwise puts its event label back over the one above.
      var wanted = trigger.getAttribute('data-ticket-select') || '';
      var all = options();
      var match = all.filter(function (o) { return wanted && o.getAttribute('data-ticket-event-id') === wanted; })[0]
        || all.filter(function (o) { return o.classList.contains('is-active'); })[0];
      if (match) selectOption(match);
      open(trigger);
    }

    // One label for the same purchase however it was opened. It used to come from
    // the trigger, so a buyer whose page auto-reloaded across a tariff boundary
    // (no trigger involved) got the PRODUCT title instead of the event title —
    // the same event under two names in the order feed and in the sheet.
    function eventLabel(fromTrigger) {
      return modalEl.getAttribute('data-ticket-event-label')
        || fromTrigger
        || modalEl.getAttribute('data-ticket-product-title')
        || '';
    }

    function open(opener) {
      if (state.open) return;
      // Never show prices this page is no longer entitled to quote.
      if (tariffExpired()) { reloadForNewTariff(true); return; }
      state.open = true;
      state.lastOpener = opener || null;
      state.sideEffectsDone = false;

      clearError();
      setSubmitting(false);
      setQuantity(state.quantity);
      applyLoginState();
      // Through eventLabel(), not straight to the product title: this is the path
      // taken by ?ticket_modal=1 and by the tariff reload, where there is no
      // trigger — and it was labelling the same purchase with the product name
      // while a click on the button labelled it with the event's.
      if (!state.eventLabel) state.eventLabel = eventLabel('');

      modalEl.hidden = false;
      requestAnimationFrame(function () {
        modalEl.classList.add('is-open');
        modalEl.setAttribute('aria-hidden', 'false');
      });

      document.documentElement.classList.add('is-ticket-modal-open');
      document.body.classList.add('is-ticket-modal-open');

      // The selected ticket, not the close button — which is what came first in
      // DOM order, so the first thing a screen reader announced on opening a
      // purchase dialog was "dismiss".
      var focusable = getFocusable();
      var initial = options().filter(function (o) { return o.classList.contains('is-active'); })[0]
        || options()[0]
        || focusable[0]
        || panelEl;
      if (initial && typeof initial.focus === 'function') initial.focus({ preventScroll: true });

      document.addEventListener('keydown', onKeydown, true);
    }

    function close() {
      if (!state.open) return;
      state.open = false;

      modalEl.classList.remove('is-open');
      modalEl.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove('is-ticket-modal-open');
      document.body.classList.remove('is-ticket-modal-open');

      document.removeEventListener('keydown', onKeydown, true);

      var duration = parseDuration(window.getComputedStyle(panelEl).transitionDuration);
      var hide = function () { if (!state.open) modalEl.hidden = true; };
      if (duration > 0) setTimeout(hide, duration); else hide();

      if (state.lastOpener && typeof state.lastOpener.focus === 'function') {
        state.lastOpener.focus({ preventScroll: true });
      }
      state.lastOpener = null;

      // Scrolling is possible again only now that the lock class is gone.
      if (deepLinkHash) {
        jumpToHash(deepLinkHash);
        deepLinkHash = '';
      }
    }

    function parseDuration(value) {
      if (!value) return 0;
      var first = String(value).split(',')[0].trim();
      var seconds = parseFloat(first);
      if (isNaN(seconds)) return 0;
      return first.indexOf('ms') !== -1 ? seconds : seconds * 1000;
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;

      var focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); panelEl.focus({ preventScroll: true }); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === panelEl || !panelEl.contains(active)) {
          e.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last || !panelEl.contains(active)) {
        // `!contains` matters here too: a stepper button that disables itself
        // under the keyboard drops focus to <body>, and forward-Tab used to walk
        // straight out into the page behind the modal.
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    function getFocusable() {
      return Array.prototype.filter.call(panelEl.querySelectorAll(FOCUSABLE), function (el) {
        return !el.hasAttribute('disabled') && el.offsetParent !== null;
      });
    }

    /* ── Submit ── */

    // Seeds the checkout's contact field with the address the buyer already gave
    // us, so they would have to clear a filled field to check out with a phone
    // number instead — which is how 20 orders ended up with no email at all.
    //
    // A convenience only, and known to be unreliable in both directions: express
    // wallet buttons overwrite it with the wallet's own address by Shopify's
    // design, and Shopify has an open regression (staff-confirmed 2026-06,
    // unfixed as of 2026-07-28) breaking this prefill outright. The authoritative
    // copy of the address is the private line item property; see the file header.
    function checkoutUrl(email) {
      var url = root() + 'checkout';
      if (!email) return url;
      return url + '?checkout%5Bemail%5D=' + encodeURIComponent(email);
    }

    // Distinguishes one press of the button from the next, so a retry after a
    // network error is recognisable as the same intent rather than a second sale.
    function submissionId() {
      return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    }

    function onSubmit(e) {
      e.preventDefault();
      if (state.submitting) return;
      if (submitEl && submitEl.hasAttribute('data-ticket-closed')) return;

      // Checked here and not only on open: the modal can sit open across the
      // deadline, and this is the last point before money changes hands.
      if (tariffExpired()) {
        showError(t('ticket_modal.error_stale', 'Ціна щойно змінилася — оновлюємо…'));
        setSubmitting(true);
        saveForm();
        setTimeout(function () { reloadForNewTariff(true); }, 1800);
        return;
      }

      var name = nameEl ? nameEl.value.trim() : '';
      var email = emailEl ? emailEl.value.trim() : '';

      if (!state.variantId) {
        return showError(t('ticket_modal.error_option', 'Оберіть квиток'));
      }
      if (name.length < 2) {
        return showError(t('ticket_modal.error_name', 'Введіть ваше імʼя'));
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        return showError(t('ticket_modal.error_email', 'Введіть коректний email'));
      }

      clearError();
      setSubmitting(true);
      saveForm();

      var submission = submissionId();

      // Two sets of keys on purpose. The localized ones are what a human reads in
      // the cart drawer; the underscore-prefixed ones are the machine-readable
      // record, ASCII and identical on every locale, hidden from checkout, and the
      // only copy of the address that an express wallet cannot overwrite. The
      // buyer's email is deliberately NOT among the visible ones — it was being
      // printed in the cart drawer for anyone looking at the screen.
      var properties = {};
      if (state.eventLabel) properties[t('ticket_modal.prop_event', 'Подія')] = state.eventLabel;
      properties[t('ticket_modal.prop_attendee', 'Імʼя гостя')] = name;
      properties[PROP_ATTENDEE] = name;
      properties[PROP_EMAIL] = email;
      properties[PROP_SUBMISSION] = submission;
      if (state.eventId) properties[PROP_EVENT_ID] = state.eventId;

      // Abandoning checkout leaves the line in the cart, so a second attempt used
      // to stack on top of the first — the buyer came back to pay for six tickets
      // they never asked for. The modal always represents one decision, so any
      // earlier line for this product is dropped before the new one is added.
      dropExistingTicketLines()
        .then(function () {
          return fetch(root() + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: state.variantId, quantity: state.quantity, properties: properties })
          });
        })
        .then(function (res) {
          // The unparseable body is tolerated only on the error path, where it is
          // just a missing explanation. A 200 that is not JSON is NOT a sale: a
          // password page or a login redirect answers 200 with HTML, and swallowing
          // that sent the buyer to checkout with an empty cart while the sheet
          // recorded a ticket.
          //
          // What proves the sale is a line, not a cart: posting a single `id`
          // answers with the added LINE ITEM (`{id, variant_id, key, …}`), and
          // only the `items:[…]` form answers with something cart-shaped. Demanding
          // `body.items` therefore rejected every successful add.
          return res.json().catch(function () { return null; }).then(function (body) {
            if (!res.ok) throw cartError(res, body);
            var line = body && body.items ? body.items[0] : body;
            if (!line || !line.key) throw new Error('cart/add returned no line');
            return body;
          });
        })
        .then(function () {
          // Cart-level copy of the same address. It reaches the order's Additional
          // details, where the ticketing app's dashboard and a human can both see
          // it, and the cart drawer reads it back to keep the checkout prefill
          // working for a buyer who wandered off and returned.
          return setCartAttributes(name, email);
        })
        .then(function () {
          // Only now, with a real line in a real cart. These used to run before
          // the add: a failed add left a subscriber tagged for an event and a row
          // in the sheet claiming a ticket nobody bought, and every retry added
          // another.
          if (!state.sideEffectsDone) {
            state.sideEffectsDone = true;
            mirrorToSheets(name, email, submission);
            subscribeToNewsletter(name, email);
          }
          clearForm();
          window.location.href = checkoutUrl(email);
        })
        .catch(function (err) {
          if (window.console && window.console.warn) window.console.warn('[ticket-modal]', err.message);
          setSubmitting(false);
          showError(err.shopperMessage
            || t('ticket_modal.error_generic', 'Не вдалося додати квиток. Спробуйте ще раз.'));
        });
    }

    // cart/add.js explains itself in the body — "only 2 left", "sold out" — and
    // that was being thrown away in favour of "try again", which is advice that
    // cannot work for a sold-out variant.
    function cartError(res, body) {
      var err = new Error('cart/add ' + res.status);
      var described = body && (body.description || body.message);
      if (res.status === 422) {
        err.shopperMessage = described
          || t('ticket_modal.error_sold_out', 'Цих квитків уже немає в наявності.');
      }
      return err;
    }

    // Never rejects: the line and its private properties are already in the cart
    // by this point, so a failure here costs the prefill, not the purchase.
    function setCartAttributes(name, email) {
      var attributes = {};
      attributes[ATTR_EMAIL] = email;
      attributes[ATTR_ATTENDEE] = name;

      return fetch(root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () { return null; });
    }

    // Removes every cart line belonging to the ticket product. Never rejects: a
    // failure here must not stop the purchase, it only risks a stale line.
    //
    // Done one line at a time through cart/change.js, which documents a line item
    // `key` as a valid id. cart/update.js takes an object of VARIANT ids, so
    // handing it line keys was liable to succeed while changing nothing — and the
    // anti-stacking guarantee above would have quietly stopped holding.
    function dropExistingTicketLines() {
      var ids = productIds();
      if (!ids.length) return Promise.resolve();

      return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var keys = (cart.items || [])
            .filter(function (item) { return ids.indexOf(String(item.product_id)) !== -1; })
            .map(function (item) { return item.key; });

          // Sequentially: each change renumbers the cart, so parallel writes to it
          // race each other.
          return keys.reduce(function (chain, key) {
            return chain.then(function () {
              return fetch(root() + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
              });
            });
          }, Promise.resolve());
        })
        .catch(function () { return null; });
    }

    // Consent is recorded here rather than at checkout on purpose: the checkout's
    // own opt-in never appears on the Apple Pay / Shop Pay express path, so relying
    // on it would silently lose consent for the fastest way to buy. Mirrors the
    // join modal's submitContact.
    function subscribeToNewsletter(name, email) {
      if (state.acceptsMarketing) return;
      if (!newsletterEl || !newsletterEl.checked || !email) return;

      var params = new URLSearchParams();
      params.set('form_type', 'customer');
      params.set('utf8', '\u2713');
      params.set('contact[email]', email);
      // Built from the event setting, the way join-modal.js does it. This was
      // hardcoded to `ticket,galas-10`, so every buyer of every future event was
      // filed under the summer 2026 festival.
      params.set('contact[tags]', state.eventId ? 'ticket,event-' + state.eventId : 'ticket');
      // Never send "false": it could revoke consent for an already-subscribed email.
      params.set('contact[accepts_marketing]', 'true');
      if (name) {
        var parts = name.split(/\s+/);
        params.set('contact[first_name]', parts[0]);
        if (parts.length > 1) params.set('contact[last_name]', parts.slice(1).join(' '));
      }

      // `fetch` rejects asynchronously, so try/catch alone never sees a network
      // failure — it surfaced as an unhandled rejection on every offline submit.
      try {
        fetch(root() + 'contact#contact_form', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'text/html' },
          body: params.toString()
        }).catch(function () { /* consent is best-effort; never block the purchase */ });
      } catch (_) { /* consent is best-effort; it must never block the purchase */ }
    }

    function mirrorToSheets(name, email, submission) {
      if (!sheets.endpoint || !sheets.token) return;
      var payload = {
        token: sheets.token,
        form: 'ticket',
        // Lets the sheet recognise a repeat of the same purchase instead of
        // recording it as another ticket sold.
        submission: submission || '',
        name: name,
        email: email,
        event: state.eventLabel,
        eventId: state.eventId,
        ticket: state.duration,
        quantity: state.quantity,
        newsletter: newsletterEl ? !!newsletterEl.checked : false,
        page: window.location.href
      };
      try {
        // text/plain keeps the request "simple" (no CORS preflight); a failure here
        // must never stop the purchase, so the result is deliberately ignored.
        fetch(sheets.endpoint, {
          method: 'POST',
          mode: 'no-cors',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify(payload)
        }).catch(function () { /* ignore */ });
      } catch (_) { /* ignore */ }
    }

    function setSubmitting(isSubmitting) {
      state.submitting = isSubmitting;
      // `data-ticket-closed`: a choice of events with every city closed. The
      // server rendered the button disabled; reopening must not re-enable it.
      if (submitEl) submitEl.disabled = isSubmitting || submitEl.hasAttribute('data-ticket-closed');
      if (!submitLabelEl) return;
      if (isSubmitting) {
        submitLabelEl.dataset.idleLabel = submitLabelEl.dataset.idleLabel || submitLabelEl.textContent;
        submitLabelEl.textContent = t('ticket_modal.submitting', 'Готуємо квиток…');
      } else if (submitLabelEl.dataset.idleLabel) {
        submitLabelEl.textContent = submitLabelEl.dataset.idleLabel;
      }
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    init();
    return { key: modalKey, openFromTrigger: openFromTrigger };
  }

  /*
    Which modal `?ticket_modal=` asks for: '1' (any truthy legacy value) is the
    festival one, '' in the registry; anything else names a modal by its key.
    Null when the parameter is absent.
  */
  function deepLinkKey() {
    var raw = new URLSearchParams(window.location.search).get('ticket_modal');
    if (!raw) return null;
    return raw === '1' || raw === 'true' ? '' : raw;
  }

  function boot() {
    document.querySelectorAll('[data-ticket-modal]').forEach(function (el) {
      var key = el.getAttribute('data-ticket-modal-key') || '';
      if (registry[key]) return;
      registry[key] = createTicketModal(el);
    });

    // One listener for every modal. A trigger whose modal is not on the page —
    // its sale closed, so the snippet rendered nothing — is left alone rather
    // than swallowed, so a link-shaped trigger still follows its href.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-ticket-trigger]') : null;
      if (!trigger) return;
      var instance = registry[trigger.getAttribute('data-ticket-trigger') || ''];
      if (!instance) return;
      e.preventDefault();
      instance.openFromTrigger(trigger);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
